import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { configureApiClient } from '../api/client';
import type { LoginResponse, RefreshResponse, UserInfo } from '../types/api';

interface AuthContextValue {
  user: UserInfo | null;
  isLoading: boolean;
  login: (response: LoginResponse) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Module-level token state — never in localStorage
let accessToken: string | null = null;
let tokenExpiresAt: number = 0; // epoch ms
let refreshPromise: Promise<string | null> | null = null;

// The email we expect this tab's session to belong to.
// Set on login and on init (from sessionStorage). Detects cross-tab cookie bleed.
let expectedEmail: string | null = null;

// Registered by AuthProvider so module-level code can trigger a React state update.
let onSessionInvalidated: () => void = () => {};

const SESSION_USER_KEY = 'hv_user';

// Cross-tab messaging — alerts other tabs when a login/logout occurs so they
// can react before the next token refresh cycle.
type AuthBroadcast =
  | { type: 'login'; email: string }
  | { type: 'logout' };

let broadcastChannel: BroadcastChannel | null = null;
try {
  broadcastChannel = new BroadcastChannel('hv_auth');
} catch {
  // BroadcastChannel unavailable (old browser, test env)
}

function broadcastAuth(msg: AuthBroadcast) {
  try { broadcastChannel?.postMessage(msg); } catch { /* ignore */ }
}

/**
 * Decode a JWT payload without verifying the signature.
 * Used ONLY to read the identity claim for mismatch detection — never for authorization.
 */
function decodeJwtEmail(token: string): string | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    // ASP.NET Core maps ClaimTypes.Email to 'email' in the JWT outbound claim map
    return (
      payload['email'] ??
      payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ??
      null
    );
  } catch {
    return null;
  }
}

function saveUserToSession(user: UserInfo) {
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
}

function loadUserFromSession(): UserInfo | null {
  try {
    const raw = sessionStorage.getItem(SESSION_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  sessionStorage.removeItem(SESSION_USER_KEY);
  accessToken = null;
  tokenExpiresAt = 0;
  expectedEmail = null;
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) return null;
    const data: RefreshResponse = await response.json();

    // Cross-tab cookie bleed guard: if we know who this tab's session belongs to,
    // verify the refreshed token is for the same user. Another tab logging in as
    // a different account will overwrite the shared httpOnly refresh cookie, so
    // the next refresh here would silently return a token for the wrong person.
    if (expectedEmail) {
      const refreshedEmail = decodeJwtEmail(data.accessToken);
      if (refreshedEmail && refreshedEmail !== expectedEmail) {
        onSessionInvalidated();
        return null;
      }
    }

    accessToken = data.accessToken;
    tokenExpiresAt = new Date(data.expiresAt).getTime();
    return accessToken;
  } catch {
    return null;
  }
}

/** Returns a valid access token, refreshing if needed. Exported for use by SignalR connections. */
export async function getAccessToken(): Promise<string | null> {
  // If token is valid for more than 60 seconds, use it
  if (accessToken && Date.now() < tokenExpiresAt - 60_000) {
    return accessToken;
  }

  // Deduplicate concurrent refresh requests
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(loadUserFromSession);
  const [isLoading, setIsLoading] = useState(true);
  const initialized = useRef(false);

  const handleUnauthorized = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  // Wire up the API client once
  useEffect(() => {
    configureApiClient(getAccessToken, handleUnauthorized);
  }, [handleUnauthorized]);

  // Register the module-level invalidation callback so refreshAccessToken()
  // can trigger a React state update when it detects a cross-tab identity mismatch.
  useEffect(() => {
    onSessionInvalidated = () => {
      clearSession();
      setUser(null);
    };
    return () => { onSessionInvalidated = () => {}; };
  }, []);

  // Listen for cross-tab login/logout events.
  // Proactively clears this tab's session rather than waiting for the next refresh cycle.
  useEffect(() => {
    if (!broadcastChannel) return;

    const handleMessage = (event: MessageEvent<AuthBroadcast>) => {
      const msg = event.data;

      if (msg.type === 'logout') {
        // Another tab revoked the shared refresh cookie — this tab's session is invalid.
        clearSession();
        setUser(null);
        return;
      }

      if (msg.type === 'login') {
        // Another tab logged in. If it's a different user, the shared cookie now
        // belongs to them — clear this tab's session immediately.
        const current = loadUserFromSession();
        if (current && msg.email !== current.email) {
          clearSession();
          setUser(null);
        }
      }
    };

    broadcastChannel.addEventListener('message', handleMessage);
    return () => broadcastChannel?.removeEventListener('message', handleMessage);
  }, []);

  // Silent auth on mount — try to restore session from refresh token cookie
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      // Prime expectedEmail from sessionStorage BEFORE refreshing so the mismatch
      // guard in refreshAccessToken() can fire on the very first call.
      const cached = loadUserFromSession();
      if (cached) {
        expectedEmail = cached.email;
      }

      const token = await refreshAccessToken();
      if (token) {
        // Token refreshed — user info should be in sessionStorage
        if (cached) {
          setUser(cached);
        } else {
          // Cookie was valid but no cached user info — force re-login
          clearSession();
          setUser(null);
        }
      } else {
        clearSession();
        setUser(null);
      }
      setIsLoading(false);
    })();
  }, []);

  const login = useCallback((response: LoginResponse) => {
    accessToken = response.accessToken;
    tokenExpiresAt = new Date(response.expiresAt).getTime();
    expectedEmail = response.email;
    const info: UserInfo = {
      email: response.email,
      firstName: response.firstName,
      lastName: response.lastName,
      isAdmin: response.isAdmin ?? false,
    };
    saveUserToSession(info);
    setUser(info);
    // Notify other tabs so they can detect identity mismatch immediately
    broadcastAuth({ type: 'login', email: response.email });
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/revoke', {
        method: 'POST',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        credentials: 'include',
      });
    } catch {
      // best-effort
    }
    // Notify other tabs that the shared refresh cookie has been revoked
    broadcastAuth({ type: 'logout' });
    clearSession();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
